"use client";

import { type FC, type Ref, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentViewer } from "@huub-web/shared";
import type { Timeout } from "@huub-web/ui";
import { useMaybePubSub } from "@huub-web/ui/hooks/usePubSub";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";

/**
 * Props for DocumentViewerTemplate
 */
export interface DocumentViewerTemplateProps {
  signEndpoint?: string;
  downloadEndpoint?: string;
  viewEndpoint?: string;
  signed?: boolean;
  variant?: "default" | "compact";
  title?: string;
  description?: string;
  value?: string;
  documentTitle?: string;
  documentSubtitle?: string;
  acknowledgementLabel?: string;
  readLabel?: string;
}

/**
 * DocumentViewer template component with signing and download capabilities
 *
 * @param props - DocumentViewerTemplate props
 * @returns JSX.Element
 */
export const DocumentViewerTemplate: FC<
  DocumentViewerTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  signEndpoint,
  downloadEndpoint,
  viewEndpoint,
  title,
  documentTitle,
  description,
  acknowledgementLabel,
  readLabel,
  signed: signedProp,
  ref,
}) => {
  const { t } = useTranslation();
  const { $get, $post, downloadDocument } = useTemplates();
  const pubSub = useMaybePubSub();

  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<Timeout | null>(null);
  const [isSigned, setIsSigned] = useState(signedProp);

  // The document view endpoint authenticates via the X-Id-Token header
  // (backend VerifyToken.verify_view) and deliberately does NOT accept the
  // token in the URL. An <iframe src> cannot send that header, so we fetch the
  // document through the authed client and render the bytes as a blob URL
  // instead. `viewEndpoint` arrives as an absolute URL; the authed client
  // prepends its own base, so we pass the pathname.
  const [viewSrc, setViewSrc] = useState<string | undefined>(undefined);
  const [isViewLoading, setIsViewLoading] = useState(false);

  useEffect(() => {
    if (!viewEndpoint) {
      setViewSrc(undefined);
      setIsViewLoading(false);
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;

    const relativePath = (() => {
      try {
        const parsed = new URL(viewEndpoint);
        return `${parsed.pathname}${parsed.search}`;
      } catch {
        return viewEndpoint; // already relative
      }
    })();

    // Drop any previously-loaded document before fetching the new one: the old
    // blob URL is revoked by this effect's cleanup, so the iframe must not keep
    // pointing at it while (or after) the new endpoint loads.
    setViewSrc(undefined);
    setIsViewLoading(true);
    $get(relativePath, { responseType: "blob" })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const blob = response?.data as Blob | undefined;
        if (!blob) {
          throw new Error("Empty document response");
        }
        objectUrl = URL.createObjectURL(blob);
        setViewSrc(objectUrl);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setViewSrc(undefined);
        console.error(
          "[DocumentViewerTemplate] Failed to load document view",
          error,
        );
        pubSub?.publish("snackbar", {
          body: t(
            "templating.document.viewError",
            "Het document kon niet worden geladen.",
          ),
          variant: "error",
        });
      })
      .finally(() => {
        if (!cancelled) {
          setIsViewLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [viewEndpoint, $get, pubSub, t]);

  const docTitle = documentTitle ?? title;
  const viewerContentItem = useMemo(
    () =>
      docTitle
        ? {
            type: "data-item" as const,
            label: docTitle,
            ...(description ? { description } : {}),
            meta: {
              ...(isSigned != null ? { ondertekend: isSigned } : {}),
              ...(signEndpoint && !isSigned ? { actie: "ondertekenen" } : {}),
            },
          }
        : null,
    [docTitle, description, isSigned, signEndpoint],
  );
  usePageContent("doc-viewer", viewerContentItem);
  const signContentItem = useMemo(
    () =>
      signEndpoint && !isSigned && docTitle
        ? {
            type: "cta" as const,
            label: t("templating.document.sign", "Ondertekenen"),
          }
        : null,
    [signEndpoint, isSigned, docTitle, t],
  );
  usePageContent("doc-sign", signContentItem);

  const handleSign = () => {
    if (!signEndpoint) {
      console.error("[DocumentViewerTemplate] No sign endpoint provided");
      return;
    }

    setIsLoading(true);

    timeoutRef.current = setTimeout(() => {
      $post(signEndpoint, {})
        .then(() => {
          setIsSigned(true);
        })
        .catch(() => {
          pubSub?.publish("snackbar", {
            body: t("templating.document.signError"),
            variant: "error",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  };

  const handleDownload = () => {
    if (!downloadEndpoint) {
      console.error("[DocumentViewerTemplate] No download endpoint provided");
      return;
    }
    downloadDocument(downloadEndpoint);
  };

  return (
    <DocumentViewer
      ref={ref}
      viewEndpoint={viewSrc}
      title={title}
      documentTitle={documentTitle}
      description={description}
      acknowledgementLabel={acknowledgementLabel}
      readLabel={readLabel}
      loading={isLoading || isViewLoading}
      signed={isSigned}
      download={!!downloadEndpoint}
      signing={!!signEndpoint}
      onSign={handleSign}
      onDownload={handleDownload}
    />
  );
};
